import React, { useEffect, useRef } from 'react';
/**
 * ConfettiCelebration — Lightweight canvas confetti burst.
 *
 * Fires a burst of colorful particles on mount, then auto-cleans up.
 * Uses requestAnimationFrame for smooth 60fps animation.
 * No external dependencies.
 */
interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: string;
  rotation: number;
  rotationSpeed: number;
  opacity: number;
  shape: 'circle' | 'rect' | 'star';
}
const COLORS = ['#F97316', '#FB923C', '#FBBF24', '#34D399', '#818CF8', '#F472B6', '#60A5FA', '#A78BFA' // violet-400
];
interface ConfettiCelebrationProps {
  /** Number of particles to spawn */
  particleCount?: number;
  /** Duration in ms before fade-out begins */
  duration?: number;
  /** Additional className for the canvas container */
  className?: string;
}
export function ConfettiCelebration({
  particleCount = 80,
  duration = 2500,
  className = ''
}: ConfettiCelebrationProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // Size canvas to container
    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };
    resize();
    const w = canvas.offsetWidth;
    const h = canvas.offsetHeight;
    // Create particles — burst from center-top
    const particles: Particle[] = Array.from({
      length: particleCount
    }, () => {
      const angle = Math.random() * Math.PI * 2;
      const speed = 2 + Math.random() * 6;
      const shapes: Particle['shape'][] = ['circle', 'rect', 'star'];
      return {
        x: w / 2 + (Math.random() - 0.5) * 60,
        y: h * 0.3 + (Math.random() - 0.5) * 40,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 3 - Math.random() * 4,
        size: 4 + Math.random() * 6,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 12,
        opacity: 1,
        shape: shapes[Math.floor(Math.random() * shapes.length)]
      };
    });
    const startTime = performance.now();
    const animate = (now: number) => {
      const elapsed = now - startTime;
      const fadeProgress = Math.max(0, (elapsed - duration) / 800);
      ctx.clearRect(0, 0, w, h);
      let alive = 0;
      for (const p of particles) {
        p.x += p.vx;
        p.vy += 0.12; // gravity
        p.y += p.vy;
        p.vx *= 0.99; // air resistance
        p.rotation += p.rotationSpeed;
        p.opacity = Math.max(0, 1 - fadeProgress);
        if (p.opacity <= 0 || p.y > h + 20) continue;
        alive++;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation * Math.PI / 180);
        ctx.globalAlpha = p.opacity;
        ctx.fillStyle = p.color;
        if (p.shape === 'circle') {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        } else if (p.shape === 'rect') {
          ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
        } else {
          // star
          const s = p.size / 2;
          ctx.beginPath();
          for (let i = 0; i < 5; i++) {
            const a = i * 4 * Math.PI / 5 - Math.PI / 2;
            const method = i === 0 ? 'moveTo' : 'lineTo';
            ctx[method](Math.cos(a) * s, Math.sin(a) * s);
          }
          ctx.closePath();
          ctx.fill();
        }
        ctx.restore();
      }
      if (alive > 0 && fadeProgress < 1) {
        animRef.current = requestAnimationFrame(animate);
      }
    };
    animRef.current = requestAnimationFrame(animate);
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [particleCount, duration]);
  return <canvas ref={canvasRef} className={`absolute inset-0 w-full h-full pointer-events-none z-50 ${className}`} aria-hidden="true" data-id="element-33" />;
}